import * as ActionTypes from '../actions';
import * as DownloadTypes from '../actions/download';
import produce from 'immer';

const defaultDownloadState = { isDownloading: false, id: 0 };
const defaultState = { player: null, survey: {}, download: defaultDownloadState };

// Shallow-clone only the path from `survey` down to `entities.answers` /
// `entities.responseTypes`, sharing every untouched branch by reference. immer's
// produce() proxies and recursively finalizes the entire normalized ASQ graph
// (sections/questions/responseTypes/answers) on each answer change — a ~7s
// main-thread block per click on large questionnaires. These hot paths only
// touch two entity maps, so a targeted spread is O(1) instead of O(graph).
const withEntities = (state, entitiesPatch) => ({
  ...state,
  survey: {
    ...state.survey,
    entities: {
      ...state.survey.entities,
      ...entitiesPatch,
    },
  },
});

const setAnswer = (state, answer) => {
  const { entities } = state.survey;
  return withEntities(state, {
    answers: { ...(entities.answers || {}), [answer.id]: answer },
    responseTypes: {
      ...entities.responseTypes,
      [answer.asqResponseTypeId]: {
        ...entities.responseTypes[answer.asqResponseTypeId],
        answer: answer.id,
      },
    },
  });
};

const removeAnswer = (state, { asqResponseTypeId, id }) => {
  const { entities } = state.survey;
  const answers = { ...(entities.answers || {}) };
  delete answers[id];
  return withEntities(state, {
    answers,
    responseTypes: {
      ...entities.responseTypes,
      [asqResponseTypeId]: {
        ...entities.responseTypes[asqResponseTypeId],
        answer: null,
      },
    },
  });
};

const posts = (state = defaultState, action) => {
  switch (action.type) {
    case ActionTypes.ANSWER_UPDATE_RECEIVE:
      // Update never changes the response-type link, only the answer payload.
      return withEntities(state, {
        answers: {
          ...(state.survey.entities.answers || {}),
          [action.payload.id]: action.payload,
        },
      });
    case ActionTypes.ANSWER_CREATE_RECEIVE:
      return setAnswer(state, action.payload);
    case ActionTypes.ANSWER_DELETE_RECEIVE:
      return removeAnswer(state, action.meta);
    default:
      break;
  }

  // Rare actions that replace whole slices — immer is fine here.
  return produce(state, draft => {
    /* eslint-disable default-case */
    switch (action.type) {
      case ActionTypes.START_SURVEY_RECEIVE:
        draft.survey = action.payload;
        return;
      case ActionTypes.PLAYER_RECEIVE:
        draft.player = action.payload;
        return;
      case DownloadTypes.ASQ_REPORT_REQUEST:
        return { ...state, download: { isDownloading: true, id: action.meta.id } };
      case DownloadTypes.ASQ_REPORT_RECEIVE:
        return { ...state, download: defaultDownloadState };
      case ActionTypes.SURVEY_UPDATE_RECEIVE:
        draft.survey.entities.surveys[action.payload.id].status = 'finished';
        draft.survey.entities.surveys[action.payload.id].asqJid = action.payload.asqJid;
        return;
    }
  });
};

export default posts;
